import { useRequirement } from '@capsicum/features/regulations/shared';
import {
	RequirementCompletionEventsEnum,
	type Requirement as RequirementType,
	useTrackRequirementsEvents
} from '@capsicum/socios-core';
import { useDeniedRequirementsModal } from '@capsicum/socios-core-web';
import { useCallback, useEffect } from 'react';
import { match } from 'ts-pattern';

import { AmlQuestionnaire } from '../aml-questionnaire/AmlQuestionnaire';
import { CredentialGuardLoader } from '../CredentialGuardLoader';
import { DomesticRinggitBorrowingStatement } from '../domestic-ringgit-borrowing/DomesticRinggitBorrowingStatement';
import { EmailVerification } from '../email-verification/EmailVerification';
import { GenericAgreement } from '../GenericAgreement';
import { ItalianOam } from '../italian-oam/ItalianOam';
import { MobileVerification } from '../mobile-verification/MobileVerification';
import { ProofOfAddress } from '../postal-address-verification/ProofOfAddress';
import { ProofOfIdentitySumsub } from '../proof-of-id-sumsub/ProofOfIdentitySumsub';
import { SourceOfIncome } from '../source-of-income/SourceOfIncome';
import { TurkishExtraFields } from '../TurkishExtraFields';
import { UkCooldown } from '../uk-regulations/uk-cooldown/UkCooldown';
import { UkQuiz } from '../uk-regulations/uk-quiz/UkQuiz';
import { UkRiskWarning } from '../uk-regulations/uk-risk-warning/UkRiskWarning';
import { UkSelfCategorisationAssessment } from '../uk-regulations/uk-self-categorisation/assessment/UkSelfCategorisationAssessment';
import { UpdateApp } from '../UpdateApp';

import type {
	RequirementIdentifier,
	UserAMLQuestionnaireRequirementConfig,
	UserEmailVerificationRequirementConfig,
	UserMobileVerificationRequirementConfig,
	UserProofOfAddressManualRequirementConfig,
	UserProofOfIdSumsubRequirementConfig,
	UserRequirementStateStatus,
	UserSourceOfIncomeRequirementConfig
} from '@capsicum/common';

type RequirementByIdentifierProps = {
	isForcedRequirement?: boolean;
	isUniqueRequirement?: boolean;
	onDismiss: () => void;
	requirementIdentifier: RequirementIdentifier;
};

const statusToAvoid = ['completed_allow', 'under_review', 'waiting'] as Array<UserRequirementStateStatus>;

export const Requirement = ({
	isForcedRequirement = false,
	isUniqueRequirement = false,
	onDismiss,
	requirementIdentifier
}: RequirementByIdentifierProps) => {
	const { showDeniedRequirementModal } = useDeniedRequirementsModal({ isLogoutModal: true, onDismiss });
	const { trackRequirementsCompletion } = useTrackRequirementsEvents();
	const { requirement, showLoader, status } = useRequirement({
		isForcedRequirement,
		requirementIdentifier,
		showDeniedRequirementModal
	});

	useEffect(() => {
		if (requirement && !showLoader && !statusToAvoid.includes(status as UserRequirementStateStatus)) {
			trackRequirementsCompletion(RequirementCompletionEventsEnum.started, requirementIdentifier);
		}
	}, [requirement, requirementIdentifier, showLoader, trackRequirementsCompletion, statusToAvoid]);

	const onDismissWithTracking = useCallback(() => {
		trackRequirementsCompletion(RequirementCompletionEventsEnum.ragequit, requirementIdentifier);
		onDismiss();
	}, [onDismiss, requirementIdentifier, trackRequirementsCompletion]);

	if (showLoader || !requirement) return <CredentialGuardLoader />;

	return match(requirement.type)
		.with('agreement', () => (
			<GenericAgreement
				isForcedRequirement={isForcedRequirement}
				requirement={requirement}
				onDismiss={onDismissWithTracking}
			/>
		))
		.with('proof_of_address_manual', () => (
			<ProofOfAddress
				isForcedRequirement={isForcedRequirement}
				requirement={requirement as RequirementType<UserProofOfAddressManualRequirementConfig>}
				status={status}
				onDismiss={onDismissWithTracking}
			/>
		))
		.with('malaysia_domestic_ringgit_borrowing', () => (
			<DomesticRinggitBorrowingStatement
				isForcedRequirement={isForcedRequirement}
				isUniqueRequirement={isUniqueRequirement}
				requirement={requirement}
				onDismiss={onDismissWithTracking}
			/>
		))
		.with('uk_self_categorisation', () => (
			<UkSelfCategorisationAssessment
				isForcedRequirement={isForcedRequirement}
				isUniqueRequirement={isUniqueRequirement}
				requirement={requirement}
				onDismiss={onDismissWithTracking}
			/>
		))
		.with('email_verification', () => (
			<EmailVerification
				isForcedRequirement={isForcedRequirement}
				isUniqueRequirement={isUniqueRequirement}
				requirement={requirement as RequirementType<UserEmailVerificationRequirementConfig>}
				onDismiss={onDismissWithTracking}
			/>
		))
		.with('mobile_verification', () => (
			<MobileVerification
				isForcedRequirement={isForcedRequirement}
				isUniqueRequirement={isUniqueRequirement}
				requirement={requirement as RequirementType<UserMobileVerificationRequirementConfig>}
				status={status}
				onDismiss={onDismissWithTracking}
			/>
		))
		.with('turkish_extra_fields', () => (
			<TurkishExtraFields
				isForcedRequirement={isForcedRequirement}
				requirement={requirement}
				onDismiss={onDismissWithTracking}
			/>
		))
		.with('uk_crypto_disclaimer', () => (
			<UkRiskWarning
				isForcedRequirement={isForcedRequirement}
				requirement={requirement}
				onDismiss={onDismissWithTracking}
			/>
		))
		.with('uk_quiz', () => (
			<UkQuiz
				isForcedRequirement={isForcedRequirement}
				requirement={requirement}
				status={status}
				onDismiss={onDismissWithTracking}
			/>
		))
		.with('uk_24_hour_cooldown', () => (
			<UkCooldown
				isForcedRequirement={isForcedRequirement}
				requirement={requirement}
				onDismiss={onDismissWithTracking}
			/>
		))
		.with('italian_oam', () => (
			<ItalianOam
				isForcedRequirement={isForcedRequirement}
				requirement={requirement}
				onDismiss={onDismissWithTracking}
			/>
		))
		.with('aml_questionnaire', () => (
			<AmlQuestionnaire
				isForcedRequirement={isForcedRequirement}
				isUniqueRequirement={isUniqueRequirement}
				requirement={requirement as RequirementType<UserAMLQuestionnaireRequirementConfig>}
				onDismiss={onDismissWithTracking}
			/>
		))
		.with('source_of_income', () => (
			<SourceOfIncome
				isForcedRequirement={isForcedRequirement}
				isUniqueRequirement={isUniqueRequirement}
				requirement={requirement as RequirementType<UserSourceOfIncomeRequirementConfig>}
				status={status}
				onDismiss={onDismissWithTracking}
			/>
		))
		.with('proof_of_id_sumsub', () => (
			<ProofOfIdentitySumsub
				isForcedRequirement={isForcedRequirement}
				isUniqueRequirement={isUniqueRequirement}
				requirement={requirement as RequirementType<UserProofOfIdSumsubRequirementConfig>}
				status={status}
				onDismiss={onDismissWithTracking}
			/>
		))
		.otherwise(() => <UpdateApp isForcedRequirement={isForcedRequirement} onDismiss={onDismiss} />);
};
